import { Icon } from '@bloo-team/ui'
import { FileText, Minus, Plus, ScanSearch } from '@bloo-team/ui/icons'
import { formatCell, isLogEventField, type SearchRow } from '../../search'
import { classifyValue, type EntityKind } from '../../entity-search'

/**
 * The dense results table: one row per event, pivot affordances on every
 * pivotable cell, the raw log line deferred behind an icon. All state is the
 * host's — which row is open, what a pivot does, where the raw view renders.
 */
export function ResultsTable({
  columns,
  rows,
  openRow,
  onOpenRow,
  onRawView,
  canPivot,
  onPivot,
  onEntity,
}: {
  columns: string[]
  rows: SearchRow[]
  /** The row whose detail panel is open, for the highlight state. */
  openRow: SearchRow | null
  onOpenRow: (row: SearchRow) => void
  /** Called with the field name and raw text when the LogEvent icon is hit. */
  onRawView: (field: string, text: string) => void
  canPivot: (field: string, value: unknown) => boolean
  onPivot: (field: string, value: unknown, op: '=' | '!=') => void
  /**
   * Cross-stream entity search on a cell's value (IP, user, host, domain,
   * hash …) — the investigation loop's "what else did this touch". Shown
   * only for values that classify; absent when the host has no such search.
   */
  onEntity?: (kind: EntityKind, value: string) => void
}) {
  const entityOf = (value: unknown): EntityKind | null => {
    if (!onEntity || typeof value !== 'string') return null
    const c = classifyValue(value)
    // A bare token on a table cell is too ambiguous to be a one-click search.
    return c && !c.ambiguous ? c.kind : null
  }
  return (
    <div className="dense-table-wrap q-results">
      <table className="dense-table">
        <thead>
          <tr>
            {columns.map((f) => (
              <th key={f}>{f}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              onClick={() => onOpenRow(row)}
              data-open={openRow === row || undefined}
            >
              {columns.map((f, ci) => (
                <td key={f} className={ci === 0 ? 'mono' : 'mono muted'}>
                  {/*
                    The raw line runs to kilobytes per row; as cell
                    text it dominates layout width and render cost.
                    An icon per row defers all of that until asked.
                  */}
                  {isLogEventField(f) && typeof row[f] === 'string' && row[f] ? (
                    <button
                      type="button"
                      className="row-action"
                      aria-label="View raw log event"
                      onClick={(e) => {
                        e.stopPropagation() // not a row-detail click
                        onRawView(f, row[f] as string)
                      }}
                    >
                      <Icon icon={FileText} size={13} />
                    </button>
                  ) : (
                    <>
                      {formatCell(f, row[f])}
                      {/* Pivot on the cell's VALUE: narrow to it
                          or exclude it, re-running the query. */}
                      {canPivot(f, row[f]) || entityOf(row[f]) ? (
                        <span className="cell-pivots">
                          {(() => {
                            const kind = entityOf(row[f])
                            return kind ? (
                              <button
                                type="button"
                                className="cell-pivot"
                                aria-label={`Search ${formatCell(f, row[f])} across streams`}
                                title="search across streams"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  onEntity?.(kind, row[f] as string)
                                }}
                              >
                                <Icon icon={ScanSearch} size={10} />
                              </button>
                            ) : null
                          })()}
                          {canPivot(f, row[f]) ? (
                            <>
                          <button
                            type="button"
                            className="cell-pivot"
                            aria-label={`Filter ${f} = ${formatCell(f, row[f])}`}
                            onClick={(e) => {
                              e.stopPropagation()
                              onPivot(f, row[f], '=')
                            }}
                          >
                            <Icon icon={Plus} size={10} />
                          </button>
                          <button
                            type="button"
                            className="cell-pivot"
                            aria-label={`Exclude ${f} = ${formatCell(f, row[f])}`}
                            onClick={(e) => {
                              e.stopPropagation()
                              onPivot(f, row[f], '!=')
                            }}
                          >
                            <Icon icon={Minus} size={10} />
                          </button>
                            </>
                          ) : null}
                        </span>
                      ) : null}
                    </>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
